import {
  addDateValidation,
  useSnackbar,
  DatePicker,
  formatDate,
  formatUTCDate,
  Button,
  useDialog,
} from '@fdha/web-ui-library';
import { Box, IconButton, useTheme } from '@mui/material';
import { isAfter, isSameDay, isValid } from 'date-fns';
import {
  FieldArray,
  Form,
  Formik,
  FormikErrors,
  FormikTouched,
  getIn,
} from 'formik';
import React, { useEffect, useState } from 'react';
import Icon from 'react-eva-icons';
import * as Yup from 'yup';
import { uniqWith, isEqual } from 'lodash';
import {
  PlannedPauseV2,
  useSetPlannedPausesMutation,
  useGetDeliveriesAffectedByPlannedPausesLazyQuery,
} from '@fdha/graphql-api-admin';

import AffectedDeliveries from './AffectedDeliveriesModal';

interface PlannedPauseEditProps {
  foodProgramUserId?: string;
  pauses: PlannedPauseV2[];
  onSuccess?: () => void;
  onFinish?: () => void;
  patientId: string;
}

const validationSchema = Yup.object().shape({
  pauses: Yup.array().of(
    Yup.object().shape(
      {
        startDate: Yup.date().when(['endDate', 'date'], (endDate) => {
          return isValid(endDate)
            ? addDateValidation(undefined, undefined, false, true).required(
                'Start date is required'
              )
            : addDateValidation(undefined, undefined, false, true);
        }),
        endDate: Yup.date().when(['startDate', 'date'], (startDate) => {
          return isValid(startDate)
            ? Yup.date()
                .nullable()
                .transform((_value, originalValue) => {
                  return originalValue ? new Date(originalValue) : null;
                })
                .typeError('Please insert a valid date')
                .test(
                  'is-after-start',
                  'End date must be after start date',
                  (value) => {
                    if (!value) {
                      return true;
                    }
                    return (
                      isValid(value) &&
                      (isSameDay(value, startDate) || isAfter(value, startDate))
                    );
                  }
                )
            : addDateValidation(undefined, undefined, false, true);
        }),
      },
      [['startDate', 'endDate']]
    )
  ),
});

interface PausesSchema {
  pauses: {
    id: number;
    startDate: Date | null;
    endDate: Date | null;
  }[];
}

const PlannedPauseEdit: React.FC<PlannedPauseEditProps> = ({
  pauses,
  foodProgramUserId,
  onFinish,
  onSuccess,
  patientId,
}) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [setPlannedPauses] = useSetPlannedPausesMutation();
  const [plannedPauseRange, setPlannedPauseRange] = useState<string[]>([]);
  const { openDialog, closeDialog } = useDialog();

  const [getAffectedDeliveries, { data: affectedDeliveriesData }] =
    useGetDeliveriesAffectedByPlannedPausesLazyQuery({
      variables: { patientId },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    });

  const defaultValue = { id: Math.random(), startDate: null, endDate: null };
  const initialValues: PausesSchema = pauses.length
    ? {
        pauses: pauses.map((value) => {
          const startDateStr = formatUTCDate(value.startDate || '');
          const endDateStr = value.endDate
            ? formatUTCDate(value.endDate)
            : null;
          return {
            id: Math.random(),
            startDate: startDateStr ? new Date(startDateStr) : null,
            endDate: endDateStr ? new Date(endDateStr) : null,
          };
        }),
      }
    : { pauses: [defaultValue] };

  const getError = (
    name: string,
    touched?: FormikTouched<{ startDate: string; endDate: string }>,
    error?: string | FormikErrors<{ startDate: string; endDate: string }>
  ) => {
    if (typeof error !== 'string') {
      const errorMsg = getIn(error, name);
      const hasTouched = getIn(touched, name);
      return hasTouched && errorMsg ? errorMsg : null;
    }
    return null;
  };

  const getFormattedDate = (date?: string) => {
    return formatDate(new Date(date as string), 'monthDayYear');
  };

  const handleSubmit = async (values: PausesSchema) => {
    const newPauses: PlannedPauseV2[] = uniqWith(
      values.pauses.filter((pause) => !!pause.startDate),
      isEqual
    ).map((pause) => ({
      startDate: formatDate(pause.startDate, 'monthDayYear'),
      endDate: pause.endDate ? formatDate(pause.endDate, 'monthDayYear') : null,
    }));

    try {
      await setPlannedPauses({
        variables: {
          input: {
            foodProgramUserId: foodProgramUserId || '',
            pauses: newPauses,
          },
        },
      });

      const formattedPauseRange = newPauses.map((pause) => {
        const start = getFormattedDate(pause.startDate);
        const end = pause.endDate
          ? getFormattedDate(pause.endDate)
          : 'No End Date';
        return `${start} - ${end}`;
      });
      setPlannedPauseRange(formattedPauseRange);

      await getAffectedDeliveries();

      showSnackbar({
        message: 'Planned pauses saved with success',
        severity: 'success',
      });
      onSuccess?.();
      onFinish?.();
    } catch (error) {
      showSnackbar({
        message: 'Error to set planned pauses',
        severity: 'error',
      });
      onFinish?.();
    }
  };

  useEffect(() => {
    if (!affectedDeliveriesData) {
      return;
    }
    const { partiallyWithin, within, uncovered } =
      affectedDeliveriesData.deliveriesAffectedByPlannedPauses;

    if (!partiallyWithin.length && !within.length && !uncovered.length) {
      return;
    }
    openDialog({
      title: 'Manage deliveries during planned pause',
      maxWidth: 'md',
      content: (
        <AffectedDeliveries
          uncoveredDeliveries={uncovered}
          withinDeliveries={within}
          partiallyWithinDeliveries={partiallyWithin}
          plannedPauseRange={plannedPauseRange}
          closeDialog={closeDialog}
        />
      ),
    });
  }, [affectedDeliveriesData, closeDialog, openDialog, plannedPauseRange]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form>
            <FieldArray
              name="pauses"
              render={(arrayHelpers) => (
                <Box
                  display="flex"
                  flexDirection="column"
                  rowGap={3}
                  width="fit-content"
                >
                  {values.pauses.map((pause, index) => (
                    <Box key={pause.id} display="flex">
                      <Box display="flex" columnGap={3}>
                        <DatePicker
                          title="Start Date"
                          value={values.pauses[index].startDate}
                          onChange={async (value) => {
                            await setFieldValue(
                              `pauses.${index}.startDate`,
                              value
                            );
                            setFieldTouched(`pauses.${index}.startDate`);
                          }}
                          onBlur={() =>
                            setFieldTouched(`pauses.${index}.startDate`)
                          }
                          error={
                            !!getError(
                              'startDate',
                              touched.pauses?.[index],
                              errors.pauses?.[index]
                            )
                          }
                          helperText={getError(
                            'startDate',
                            touched.pauses?.[index],
                            errors.pauses?.[index]
                          )}
                          data-testid="START_DATE_TEXT_FIELD_EDIT"
                        />
                        <DatePicker
                          title="End Date (optional)"
                          value={values.pauses[index].endDate}
                          onChange={async (value) => {
                            await setFieldValue(
                              `pauses.${index}.endDate`,
                              value
                            );
                            setFieldTouched(`pauses.${index}.endDate`);
                          }}
                          error={
                            !!getError(
                              'endDate',
                              touched.pauses?.[index],
                              errors.pauses?.[index]
                            )
                          }
                          helperText={getError(
                            'endDate',
                            touched.pauses?.[index],
                            errors.pauses?.[index]
                          )}
                          onBlur={() =>
                            setFieldTouched(`pauses.${index}.endDate`)
                          }
                          data-testid="END_DATE_TEXT_FIELD"
                        />
                      </Box>
                      <Box marginTop={5} marginLeft={4}>
                        {index === values.pauses.length - 1 && (
                          <IconButton
                            data-testid="ADD_ICON_BUTTON"
                            onClick={() => arrayHelpers.push(defaultValue)}
                          >
                            <Icon
                              name="plus-circle-outline"
                              size="large"
                              fill={theme.palette.primary.main}
                            />
                          </IconButton>
                        )}
                      </Box>
                      <Box marginTop={5}>
                        <IconButton
                          data-testid="REMOVE_ICON_BUTTON"
                          onClick={() => {
                            if (values.pauses.length === 1) {
                              arrayHelpers.replace(index, defaultValue);
                            } else {
                              arrayHelpers.remove(index);
                            }
                          }}
                        >
                          <Icon
                            name="minus-circle-outline"
                            size="large"
                            fill={theme.palette.primary.main}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            />
            <Box display="flex" justifyContent="flex-end" marginTop={5}>
              <Button
                color="primary"
                size="large"
                onClick={onFinish}
                data-testid="CANCEL_BUTTON"
              >
                CANCEL
              </Button>
              <Button
                disabled={isSubmitting}
                variant="contained"
                color="secondary"
                size="large"
                type="submit"
                data-testid="SAVE_BUTTON"
              >
                SAVE
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PlannedPauseEdit;
